import {httpService} from "@/modules/common/services";
import {convertTime12to24, testAmPm} from "@/modules/common/services/timeService";
import {createDateAsUTC} from "@/modules/common/services/timeService.js"
import moment from "moment"

const congregateWeeklyServiceAvailability = (slots) => {
  let res = slots.reduce((acc, currSlot) => {
    acc[currSlot.day].slots.push({
      start_time: `${currSlot.start_hour.toString().padStart(2,'0')}:${currSlot.start_minute.toString().padStart(2,'0')}`,
      end_time: `${currSlot.end_hour.toString().padStart(2,'0')}:${currSlot.end_minute.toString().padStart(2,'0')}`,
      id: currSlot.id,
    })
    acc[currSlot.day].enabled = true
    return acc
  }, [{enabled: false, day:0, slots:[]},{enabled: false, day:1, slots:[]},{enabled: false, day:2, slots:[]},{enabled: false, day:3, slots:[]},{enabled: false, day:4, slots:[]},{enabled: false, day:5, slots:[]},{enabled: false, day:6, slots:[]}])
  return res
}

const fetchWeeklyAvailabilities = async (weeklyAvailabilityUids, general_availability, useSelectedStaffTZ = false) => {

  let weeklyAvailabilityUidsArray = []
  weeklyAvailabilityUids.forEach((curr, index) => {
    if(index % 50 === 0) {
      weeklyAvailabilityUidsArray.push([])
    }
    weeklyAvailabilityUidsArray[weeklyAvailabilityUidsArray.length - 1].push(curr)
  })

  const callsArr = weeklyAvailabilityUidsArray.map((uids) => {
    const params = {
      uids: uids,
      general_availability
    }
    return httpService.get(`weekly_availabilities`, params);
  })

  const weeklyAvailabilities = (await Promise.all(callsArr)).flat()

  return weeklyAvailabilities.reduce((acc, curr) => {
    return {
      ...acc,
      [curr.uid]: congregateWeeklyServiceAvailability(curr.time_slots_attributes)
    }
  }, {})
}

const fetchWeeklyAvailability = async (weeklyAvailabilityUid, general_availability, useSelectedStaffTZ = false ) => {
  const weeklyAvailability = await httpService.get(`weekly_availabilities/${weeklyAvailabilityUid}`, {general_availability, use_selected_staff_time_zone: useSelectedStaffTZ});
  return weeklyAvailability.time_slots_by_day || congregateWeeklyServiceAvailability(weeklyAvailability.time_slots_attributes);
}

const setWeeklyAvailability = async (slotId, weeklyAvailabilityParms) => {
  if (testAmPm(weeklyAvailabilityParms.start_time)) {
    weeklyAvailabilityParms.start_time = convertTime12to24(weeklyAvailabilityParms.start_time);
  }
  if (testAmPm(weeklyAvailabilityParms.end_time)) {
    weeklyAvailabilityParms.end_time = convertTime12to24(weeklyAvailabilityParms.end_time);
  }

  weeklyAvailabilityParms.new_api = true
  weeklyAvailabilityParms.uid = slotId
  const weeklyAvailability = await httpService.put(`/business/scheduling/v1/time_slots/${slotId}`, weeklyAvailabilityParms);
  return weeklyAvailability.data.general_availability.time_slots_by_day;
}

const deleteWeeklyAvailabilitySlots = async (slotIds, useSelectedStaffTimeZone) => {
  const promisesAvailabilitySlots = slotIds.map(slotId => httpService.remove(`/business/scheduling/v1/time_slots/${slotId}`, {data: {new_api: true, use_selected_staff_time_zone: useSelectedStaffTimeZone}}));
  const weeklyAvailabilities = []
  await Promise.all(promisesAvailabilitySlots.map(p =>
    p.then(weeklyAvailability => {
        weeklyAvailabilities.push(weeklyAvailability);
      }
    )));
  // return the last weekly availability that returns from the server
  return weeklyAvailabilities.at(-1).data.general_availability.time_slots_by_day;
}

const createWeeklyAvailability = async (weeklyAvailabilityParms) => {
  if (testAmPm(weeklyAvailabilityParms.start_time)) {
    weeklyAvailabilityParms.start_time = convertTime12to24(weeklyAvailabilityParms.start_time);
  }
  if (testAmPm(weeklyAvailabilityParms.end_time)) {
    weeklyAvailabilityParms.end_time = convertTime12to24(weeklyAvailabilityParms.end_time);
  }
  weeklyAvailabilityParms.new_api = true
  const weeklyAvailability = await httpService.post(`/business/scheduling/v1/time_slots`, weeklyAvailabilityParms);
  return weeklyAvailability.data.general_availability.time_slots_by_day;
}

const updateEnabledDay = async (weeklyAvailabilityUid, enabledDayParams) => {
  const weeklyAvailability = await httpService.put(`weekly_availabilities/${weeklyAvailabilityUid}/update_enabled_day`, enabledDayParams);
  return weeklyAvailability.data.general_availability.time_slots_by_day;
}

const groupRelevantSlotsByDate = (slots, startTime, endTime, timeZone) => {
  const slotsToDaysMap = {}

  slots.forEach(slot => {

    let currSlotStartTimeObj = moment(slot.start_time)
    let currSlotEndTimeObj = moment(slot.end_time)
    currSlotStartTimeObj.tz(timeZone)
    currSlotEndTimeObj.tz(timeZone)

    slot.startObj = currSlotStartTimeObj
    slot.endObj = currSlotEndTimeObj
    if(currSlotStartTimeObj < startTime || currSlotStartTimeObj > endTime) {
      return
    }
    let parsedDateString = currSlotStartTimeObj.format('YYYY-MM-DD')
    slotsToDaysMap[parsedDateString] = slotsToDaysMap[parsedDateString] || []
    slotsToDaysMap[parsedDateString].push(slot)
  })
  Object.values(slotsToDaysMap).forEach(slotsArray => slotsArray.sort((a,b) => +a.startObj < +b.startObj ? -1 : 1))
  return slotsToDaysMap
}

const ignoreSlotsByDateMap = (slots, timeZone) => {
  return slots.reduce((slotsToDaysMap, slot) => {
    const currSlotStartTimeObj = moment(slot.start_time).tz(timeZone)
    const parsedDateString = currSlotStartTimeObj.format('YYYY-MM-DD')

    slotsToDaysMap[parsedDateString] = slotsToDaysMap[parsedDateString] || {}
    slotsToDaysMap[parsedDateString][slot.time_slot_id] = slot

    return slotsToDaysMap
  }, {})
}

const editAvailabilitySlot = async ({timeSlotUid, weekly_availability_uid, start_time, end_time, useSelectedStaffTimeZone = false}) => {

  return await httpService.put(`time_slot_exceptions/${timeSlotUid}`, {timeSlotUid, weekly_availability_uid, start_time, end_time, use_selected_staff_time_zone: useSelectedStaffTimeZone})
}
const createAvailabilitySlot = async ({weekly_availability_uid, start_time, end_time, use_selected_staff_time_zone = false}) => {

  return await httpService.post(`time_slot_exceptions`, {weekly_availability_uid, start_time, end_time, use_selected_staff_time_zone: use_selected_staff_time_zone})
}

const fetchAvailabilityException = async (weekly_availabilities_uids, startDate, endDate, timeZone) => {
  let startDateMomentObj = moment(startDate).add(-1, 'days').startOf('day')
  let endDateMomentObj = moment(endDate).add(1, 'days').endOf('day')
  const requestParams = {
    weekly_availabilities_uids: weekly_availabilities_uids.join(', '),
    start: createDateAsUTC(startDateMomentObj.toDate()).toISOString(),
    end: createDateAsUTC(endDateMomentObj.toDate()).toISOString(),
  }

  const weeklyAvailability = await httpService.get('time_slot_exceptions/bulk', requestParams);
  const res = {};

  for (const uid in weeklyAvailability) {
    const weeklyAvailabilityException = [], weeklyAvailabilityIgnore = [];
    for (const availability of weeklyAvailability[uid]) {
      if (!!availability.operation) {
        weeklyAvailabilityIgnore.push(availability);
      } else {
        weeklyAvailabilityException.push(availability);
      }
    }
    const relevantWeeklyAvailabilityException = groupRelevantSlotsByDate(weeklyAvailabilityException, startDateMomentObj, endDateMomentObj, timeZone)
    const relevantWeeklyIgnore = ignoreSlotsByDateMap(weeklyAvailabilityIgnore, timeZone)
    res[uid] = {
      ignore: relevantWeeklyIgnore,
      exceptions: relevantWeeklyAvailabilityException,
    }
  }

  return res;
}

const deleteAvailabilitySlot = async ({timeSlotUid, timeSlotId, weekly_availability_uid, start_time, end_time, useSelectedStaffTimeZone = false}) => {
  const requestParams = {
    weekly_availability_uid,
    start_time,
    end_time,
  }
  let operation;
  if(timeSlotId) {
    requestParams.time_slot_id = timeSlotId
    requestParams.operation = "ignore"
    requestParams.use_selected_staff_time_zone = useSelectedStaffTimeZone
    operation = 'post'
  } else {
    requestParams.uid = timeSlotUid
    operation = 'remove'
  }

  const res = await httpService[operation](`time_slot_exceptions${timeSlotUid ? `/${timeSlotUid}` : ''}`, requestParams)

  return res
}

const undoIgnoreWeeklySlot = async (slot) => {
  return await httpService.remove(`time_slot_exceptions/${slot.uid}`, slot)
}

export const availabilityService = {
  fetchWeeklyAvailabilities,
  fetchWeeklyAvailability,
  setWeeklyAvailability,
  deleteWeeklyAvailabilitySlots,
  updateEnabledDay,
  createWeeklyAvailability,
  fetchAvailabilityException,
  deleteAvailabilitySlot,
  editAvailabilitySlot,
  createAvailabilitySlot,
  undoIgnoreWeeklySlot,

}
